/* =============================================================================
   hero-marquee.js: Gallery() from app/components/home/funnel/MarqueeBand.tsx
   in vanilla JS, for sections/undepleted-marquee.liquid.

   The markup ships in its initial React state (shot 0 active). This file keeps
   the same ARIA tabs behaviour:
     - click a thumb      -> it becomes the active shot
     - ArrowRight / Left  -> next / previous shot, wrapping at both ends
     - Home / End         -> first / last shot
     (keys act on the ACTIVE index, as the component's onKeyDown does, and move
      focus to the newly active tab)
   On a change: aria-selected, tabindex and the tab classes swap; the panel's
   aria-labelledby follows the active tab; the panel image is REMOUNTED (the JSX
   keys it on src), as a fresh eager <img> with the shot's own alt and fit.

   Class strings are written out in full so Tailwind's scanner emits them.
   ========================================================================== */
(function () {
  'use strict';

  // GALLERY from MarqueeBand.tsx: the panel alt and fit per shot. The src is the
  // same asset as the matching thumb, so it is read from the thumb's <img>.
  // CRO plan H2 + review pass (2026-09-15): four shots, all our own photos (lvanity, lbanner, lmacro,
  // lflat); no supplier renders and no hands-and-carton render. 'cover-right' keeps lbanner's bottle centred.
  var GALLERY = [
    {alt: 'The serum bottle on a pale stone shelf in daylight.', fit: 'cover'},
    {
      alt: 'UNDEPLETED + NAD Renewal Serum, a 30 ml red glass dropper bottle, on a stone ledge in warm light.',
      fit: 'cover-right',
    },
    {alt: "A close-up of the bottle's silver collar against the dark red glass.", fit: 'cover'},
    {alt: 'The bottle and box seen from above on a plain surface.', fit: 'cover'},
  ];

  var PANEL_CONTAIN = 'h-full w-full object-contain p-4 sm:p-6';
  var PANEL_COVER = 'h-full w-full object-cover';
  var PANEL_COVER_RIGHT = 'h-full w-full object-cover object-right';
  var TAB_SELECTED = 'aspect-square overflow-hidden border bg-paper-sunken border-ink ring-1 ring-ink';
  var TAB_IDLE = 'aspect-square overflow-hidden border bg-paper-sunken border-rule-control hover:border-ink-3';

  var bound = typeof WeakSet === 'function' ? new WeakSet() : null;

  function init() {
    var panel = document.getElementById('marquee-gallery');
    if (!panel) return;
    if (bound) {
      if (bound.has(panel)) return;
      bound.add(panel);
    }
    var tablist = panel.nextElementSibling;
    if (!tablist) return;
    var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    var last = tabs.length - 1;
    var active = 0;
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].getAttribute('aria-selected') === 'true') active = i;
    }

    function setActive(next) {
      if (next === active) return; // same state: React does not re-render
      active = next;
      for (var t = 0; t < tabs.length; t++) {
        var selected = t === next;
        tabs[t].setAttribute('aria-selected', selected ? 'true' : 'false');
        tabs[t].setAttribute('tabindex', selected ? '0' : '-1');
        tabs[t].className = selected ? TAB_SELECTED : TAB_IDLE;
      }
      panel.setAttribute('aria-labelledby', tabs[next].id);

      var shot = GALLERY[next] || {alt: '', fit: 'cover'};
      var thumb = tabs[next].querySelector('img');
      var img = document.createElement('img');
      img.setAttribute('src', thumb ? thumb.getAttribute('src') : '');
      img.setAttribute('alt', shot.alt);
      img.setAttribute('loading', 'eager');
      img.setAttribute('decoding', 'async');
      img.className =
        shot.fit === 'contain' ? PANEL_CONTAIN : shot.fit === 'cover-right' ? PANEL_COVER_RIGHT : PANEL_COVER;
      while (panel.firstChild) panel.removeChild(panel.firstChild);
      panel.appendChild(img);
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        setActive(index);
      });
      tab.addEventListener('keydown', function (event) {
        var next = -1;
        if (event.key === 'ArrowRight') next = active === last ? 0 : active + 1;
        else if (event.key === 'ArrowLeft') next = active === 0 ? last : active - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;
        if (next < 0) return;
        event.preventDefault();
        setActive(next);
        tabs[next].focus();
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Theme editor re-renders a section in place; bind the fresh markup.
  document.addEventListener('shopify:section:load', init);
})();
